import './styles/main.css';
import './lib/chartTheme';
import { supabase, Profile, initSessionTracking, forceLogout, updateSessionStart } from './lib/supabase';
import { App } from './App';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { UpdatePasswordPage } from './pages/UpdatePasswordPage';
import { renderExpiredLink } from './pages/auth/ExpiredLinkScreen';
import { promptSharedPerson } from './pages/auth/SharedPersonPrompt';
import { endBootSplash } from './pages/auth/AuthShell';
import { viewAsMe } from '../../lib/rosterIdentity.mjs';   // R120: the view-as /api/me carries inspector_names + tech_link_state

// Attach the Supabase access token to every /api request. This single interceptor
// covers ApiService and all the pages that call fetch('/api/...') directly, so the
// backend's auth middleware sees a Bearer token without touching each call site.
const nativeFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (url.startsWith('/api/')) {
    // Resilient: a session/refresh hiccup must NOT block the request entirely
    // (that left every page hanging on "could not load"). On failure we proceed
    // without a token, so the backend returns 401 and ApiService redirects to
    // login cleanly instead of failing silently.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        init.headers = { ...(init.headers as Record<string, string>), Authorization: `Bearer ${session.access_token}` };
      }
    } catch (e) {
      console.warn('[auth] getSession failed before /api fetch — proceeding without token', e);
    }
  }
  return nativeFetch(input, init);
};

console.log('=== Surge RM Starting ===');

const app = document.getElementById('app');

async function getProfile(userId: string, email: string): Promise<Profile | null> {
  // Try to get profile from database
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  
  if (profile) {
    return profile as Profile;
  }
  
  // No readable profile: an in-memory stand-in only (the server is the
  // authority on roles). R116 security: never written back — this used to
  // upsert role 'manager' into the user's own profile row.
  const defaultProfile = {
    id: userId,
    email: email,
    full_name: email.split('@')[0],
    role: 'manager',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  
  return defaultProfile as Profile;
}

async function checkAuth(): Promise<Profile | null> {
  const { data: { session } } = await supabase.auth.getSession();
  console.log('Session check - exists:', !!session);

  if (!session) {
    console.log('No session found');
    return null;
  }

  console.log('Session user:', session.user.email);

  // Resolve role + profile from the server (/api/me). The server reads with the
  // service-role key, so it is immune to the profiles-table RLS recursion that
  // makes the direct client read fail — which was silently defaulting EVERY login
  // to 'manager' and giving field roles (inspector/maintenance/cleaner) the full
  // office nav instead of their focused tool. /api/me is the authoritative role.
  try {
    const res = await fetch('/api/me');
    if (res.ok) {
      const me = await res.json();
      // R116 security: an unapproved / role-less login is turned away by the
      // server; sign it out and say why on the login screen.
      if (me && me.blocked) {
        try { sessionStorage.setItem('authNotice', me.message || 'This login is not active yet. Ask an admin.'); } catch (_) { /* private mode */ }
        await supabase.auth.signOut().catch(() => {});
        return null;
      }
      const p = me.profile || {};
      const now = new Date().toISOString();
      console.log('Profile via /api/me:', p.email || session.user.email, '→ role', me.role);
      const prof = {
        id: p.id || session.user.id,
        email: p.email || session.user.email || '',
        full_name: p.full_name || (session.user.email ? session.user.email.split('@')[0] : ''),
        role: (me.role || p.role || 'manager'),
        is_active: p.is_active !== false,
        created_at: p.created_at || now,
        updated_at: p.updated_at || now,
      } as Profile;
      // R30 #5: shared company login (Marina's crew, Fido) — the boot flow
      // will ask who's actually working and attribute as "Company — Person".
      (prof as any).shared = !!me.shared;
      return prof;
    }
    console.warn('[auth] /api/me returned', res.status, '— falling back to direct profile read');
  } catch (e) {
    console.warn('[auth] /api/me failed — falling back to direct profile read', e);
  }

  // Fallback (offline / server unreachable): direct read. May hit the RLS
  // recursion and default to manager, but at least keeps the app usable.
  const profile = await getProfile(session.user.id, session.user.email);
  console.log('Profile loaded (fallback):', profile?.email);

  return profile;
}

async function logout(): Promise<void> {
  await forceLogout();
}

// R30 #5: the who-are-you prompt for shared company logins lives in
// pages/auth/SharedPersonPrompt.ts (R116: it wears the sign-in shell).

// Small always-visible chip so a crew handing the phone around can switch the
// active person in one tap (clears the stored name and re-prompts on reload).
function mountSharedActorBadge(company: string, person: string, storageKey: string): void {
  const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  const chip = document.createElement('button');
  chip.id = 'shared-actor-chip';
  chip.title = `Working as ${person} on the ${company} login — tap to switch person`;
  // z-index BELOW every modal/sheet layer (they sit at 2000+) — a mis-tap
  // through an open modal must never trigger the reload.
  chip.style.cssText = 'position:fixed;bottom:14px;left:14px;z-index:800;display:inline-flex;align-items:center;gap:6px;padding:8px 13px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;box-shadow:0 4px 16px rgba(0,0,0,.12);font-size:12.5px;font-weight:600;color:#0f172a;cursor:pointer;';
  chip.innerHTML = `👤 ${esc(person)} <span style="color:#64748b;font-weight:500;">· switch</span>`;
  chip.addEventListener('click', () => {
    if (!confirm(`Switch to a different person? This reloads the page (unsaved carts/forms are lost) and asks who's working.`)) return;
    try { localStorage.removeItem(storageKey); } catch (_) { /* noop */ }
    window.location.reload();
  });
  document.body.appendChild(chip);
}

// A new password is set → sign out and land on the sign-in card, which says so.
async function passwordSaved(): Promise<void> {
  try { sessionStorage.setItem('authNoticeOk', 'Password saved. Sign in with your new password.'); } catch (_) { /* private mode */ }
  await logout();
  window.location.href = '/';
}

async function init() {
  if (!app) return;

  // R109 (l): Slack "Open" links — /?open=wo:<id> | task:<id> | issue:<boardId> (R115: | stay:<hostaway_id>).
  // Stash it BEFORE auth (the login callback reloads '/', which drops the
  // query; sessionStorage survives in this tab) and scrub it from the address
  // bar. App.render() turns it into the page's existing focus hand-off.
  try {
    const sp = new URLSearchParams(window.location.search);
    const open = sp.get('open');
    if (open !== null) {
      if (/^(wo|task|issue|stay):[^\r\n<>"'`]{1,200}$/.test(open)) sessionStorage.setItem('deep_open', open);
      sp.delete('open');
      const qs = sp.toString();
      history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    }
  } catch (_) { /* private mode — the link just lands on home */ }

  // Check if this is a password reset callback
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const type = hashParams.get('type');
  
  // SELF-CONTAINED sign-in links (Add user / Resend invite): the URL carries the
  // one-time token itself (#token_hash=...) and WE exchange it — no Supabase
  // redirect, Site URL, or allow-list involved (that chain repeatedly sent
  // people to the wrong place).
  const tokenHash = hashParams.get('token_hash');
  if (tokenHash && (type === 'recovery' || type === 'invite')) {
    // Scrub the hash first so a reload never retries the (consumed) token.
    try { history.replaceState(null, '', window.location.pathname); } catch (_) { /* */ }
    // (the boot splash in index.html stays up while the token is exchanged)
    const { error } = await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash });
    if (!error) {
      const updatePasswordPage = new UpdatePasswordPage(app, passwordSaved, type === 'invite' ? 'invite' : 'recovery');
      await updatePasswordPage.render();
      return;
    }
    // R116 #1: the expired / used link wears the sign-in shell too.
    renderExpiredLink(app);
    return;
  }

  // Both recovery links and first-time invite links should land the user on the
  // set-password screen (Supabase tags them type=recovery / type=invite). Without
  // handling 'invite' here, invited users were silently signed in and never set a
  // password — so they couldn't log back in normally afterward.
  if (type === 'recovery' || type === 'invite') {
    console.log('Showing update password page');
    const updatePasswordPage = new UpdatePasswordPage(app, passwordSaved, type === 'invite' ? 'invite' : 'recovery');
    await updatePasswordPage.render();
    return;
  }

  // R116 #1: ?session_expired=true (forceLogout — the 2 h idle timer) becomes a
  // soft notice on the sign-in card; the param is scrubbed so a reload or a
  // shared URL never repeats it.
  let sessionExpired = false;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_expired')) {
      sessionExpired = true;
      urlParams.delete('session_expired');
      const qs = urlParams.toString();
      history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    }
  } catch (_) { /* the notice is a nicety */ }
  
  const profile = await checkAuth();
  
  if (!profile) {
    console.log('No profile, showing login page');
    
    const showLogin = () => {
      const loginPage = new LoginPage(
        app, 
        async (loggedInProfile: Profile) => {
          console.log('Login successful callback received');
          // Store profile in session storage
          sessionStorage.setItem('userProfile', JSON.stringify(loggedInProfile));
          sessionStorage.setItem('userRole', loggedInProfile.role || 'manager');
          sessionStorage.setItem('userBaseRole', loggedInProfile.role || 'manager');   // R64: the REAL role — gates the field view switcher
          await updateSessionStart();
          initSessionTracking();
          // Force reload to ensure clean state
          window.location.href = '/';
        }, 
        () => {
          showForgotPassword();
        },
        { sessionExpired }
      );
      sessionExpired = false;   // said once — not again after a Forgot → Back round trip
      loginPage.render();
    };
    
    const showForgotPassword = () => {
      const forgotPage = new ForgotPasswordPage(app, () => {
        showLogin();
      });
      forgotPage.render();
    };
    
    showLogin();
  } else {
    console.log(`Welcome ${profile.full_name || profile.email} (${profile.role})`);
    // R30 #5: shared company login → resolve the actual PERSON on this device
    // (asked once, remembered in localStorage) and bake "Company — Person"
    // into the cached profile name. Every attribution site in the app reads
    // userProfile.full_name, so inspections, issues, EOD, orders all name the
    // real person without touching those call sites.
    if ((profile as any).shared) {
      const company = (profile.full_name || (profile.email || '').split('@')[0] || 'Team').trim();
      const key = `sharedActor:${profile.id}`;
      let person = '';
      try { person = (localStorage.getItem(key) || '').trim(); } catch (_) { /* private mode */ }
      if (!person) {
        person = await promptSharedPerson(company);
        try { localStorage.setItem(key, person); } catch (_) { /* private mode */ }
      }
      (profile as any).company_name = company;
      (profile as any).person_name = person;
      profile.full_name = `${company} — ${person}`;
      mountSharedActorBadge(company, person, key);
    }
    sessionStorage.setItem('userProfile', JSON.stringify(profile));
    // View-as (R13 #3): an admin/director can browse the app AS another role.
    // Only what the client renders changes — the server still sees the real
    // login, so permissions are unchanged. The real role is kept for the exit
    // banner, and non-privileged logins ignore any leftover viewAsRole.
    const realRole = profile.role || 'manager';
    let effectiveRole = realRole;
    let viewedName = '';
    try {
      sessionStorage.setItem('realUserRole', realRole);
      const privileged = realRole === 'admin' || realRole === 'director';
      const vaUser = sessionStorage.getItem('viewAsUserId');
      const va = sessionStorage.getItem('viewAsRole');
      sessionStorage.removeItem('viewAsContext');
      if (vaUser && privileged) {
        // View as a SPECIFIC user (R14): pull their role + roster/owner links,
        // impersonate the client-side identity chain. Pages resolve "who am I"
        // via /api/me — intercept that one call so 'assigned to me' surfaces
        // (cleaner day, tech queues) show the viewed user's work.
        const ctx = await fetch(`/api/users/${encodeURIComponent(vaUser)}/view-context`)
          .then(r => r.ok ? r.json() : null).catch(() => null);
        if (ctx) {
          effectiveRole = ctx.role || 'manager';
          viewedName = ctx.full_name || ctx.email || '';
          sessionStorage.setItem('viewAsContext', JSON.stringify(ctx));
          sessionStorage.setItem('userProfile', JSON.stringify({ id: ctx.id, email: ctx.email, full_name: ctx.full_name, role: ctx.role }));
          const origFetch = window.fetch.bind(window);
          window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
            const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
            const resp = await origFetch(input, init);
            // /api/me only (NOT /api/me/portal — that data is server-scoped).
            if (/\/api\/me(\?|$)/.test(url) && resp.ok) {
              const j = await resp.clone().json().catch(() => null);
              if (j) {
                // R120: lib/rosterIdentity viewAsMe — role / profile / tech_link /
                // shared PLUS inspector_names and tech_link_state come from the
                // viewed user's view-context (never the admin's own), so the
                // Inspections "Mine" view and the cleaner portal resolve as theirs.
                return new Response(JSON.stringify(viewAsMe(j, ctx)), { status: 200, headers: { 'Content-Type': 'application/json' } });
              }
            }
            return resp;
          };
        } else {
          sessionStorage.removeItem('viewAsUserId');
        }
      } else if (vaUser) {
        sessionStorage.removeItem('viewAsUserId');
      } else if (va && privileged && va !== realRole) {
        effectiveRole = va;
      } else if (va) {
        sessionStorage.removeItem('viewAsRole');
      }
    } catch (_) { /* sessionStorage unavailable */ }
    // R64 (Alan cleans AND does handyman work): a FIELD base role may view
    // another FIELD role — self-service, never an elevation (office roles are
    // not in the list, and admin view-as above takes precedence via the
    // effectiveRole === realRole guard). Persisted in fieldViewRole so the
    // choice survives this boot-time re-stamp of userRole.
    let fieldSwitched = false;
    try {
      const FIELD_VIEWS = ['cleaner', 'maintenance', 'inspector'];
      const fv = sessionStorage.getItem('fieldViewRole');
      if (fv && FIELD_VIEWS.includes(fv) && FIELD_VIEWS.includes(realRole) && effectiveRole === realRole) {
        effectiveRole = fv;
        fieldSwitched = true;   // self-switch — the switcher chips are the exit, no view-as banner
      }
      sessionStorage.setItem('userBaseRole', realRole);
    } catch (_) { /* sessionStorage unavailable */ }
    sessionStorage.setItem('userRole', effectiveRole);
    initSessionTracking();

    // Show dashboard (R116 #1: the boot splash's sign-in look ends here — App.render replaces it)
    endBootSplash();
    const dashboard = new App(app);
    await dashboard.render();

    // Floating exit banner while view-as is active. The R64 field self-switch
    // is exempt — its own chips are the way back, and this pill would sit on
    // top of mobile field controls.
    if ((effectiveRole !== realRole && !fieldSwitched) || viewedName) {
      const who = viewedName
        ? `${viewedName} (${effectiveRole.replace(/_/g, ' ')})`
        : effectiveRole.replace(/_/g, ' ');
      const bar = document.createElement('div');
      bar.style.cssText = 'position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:6000;background:#0f172a;color:#fff;padding:9px 16px;border-radius:999px;font-size:13px;display:flex;gap:12px;align-items:center;box-shadow:0 8px 24px rgba(0,0,0,.28);white-space:nowrap;';
      bar.innerHTML = `<span>👁 Viewing as <b style="text-transform:capitalize;"></b> · your real role is ${realRole}</span><button id="exit-viewas" style="background:#fff;color:#0f172a;border:none;border-radius:999px;padding:5px 12px;font-size:12px;font-weight:700;cursor:pointer;">Exit</button>`;
      (bar.querySelector('b') as HTMLElement).textContent = who;   // name is user data — never innerHTML it
      document.body.appendChild(bar);
      bar.querySelector('#exit-viewas')?.addEventListener('click', () => {
        try { sessionStorage.removeItem('viewAsRole'); sessionStorage.removeItem('viewAsUserId'); sessionStorage.removeItem('viewAsContext'); sessionStorage.removeItem('fieldViewRole'); } catch (_) { /* */ }
        window.location.reload();
      });
    }
    
    // Add logout button to top bar
    setTimeout(() => {
      const logoutBtn = document.getElementById('auth-logout-btn');
      if (logoutBtn) return;
      
      const logoutButton = document.createElement('button');
      logoutButton.id = 'auth-logout-btn';
      logoutButton.textContent = 'Sign Out';
      logoutButton.className = 'btn btn-outline';
      logoutButton.style.marginLeft = '12px';
      logoutButton.onclick = async () => {
        await logout();
      };
      
      const topBarActions = document.querySelector('.top-bar-actions');
      if (topBarActions) {
        topBarActions.appendChild(logoutButton);
      }
    }, 100);
  }
}

// Add styles
// R116 #1: the sign-in screens moved to pages/auth/auth.css (scoped .au-*).
// .form-group stays global here — SettingsPage and TicketsPage use it.
const styles = document.createElement('style');
styles.textContent = `
  .form-group {
    margin-bottom: 20px;
  }
  .form-group label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .form-group input, .form-group select {
    width: 100%;
    padding: 13px 16px;
    border: 1px solid #e5e9f2;
    border-radius: 12px;
    font-size: 16px;
    font-family: inherit;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .form-group input:focus, .form-group select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
  }
`;
document.head.appendChild(styles);

// Run init
init();

// Register the service worker so the app is installable and launches offline.
// Kept OUTSIDE init() (which has early returns for the recovery/login flows) so
// registration runs on every entry path. Guarded + best-effort: a failed
// registration must never block the app. The SW itself never touches /api/.
if ('serviceWorker' in navigator && (location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1')) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('[sw] registration failed', e));
  });
}

// Offline-durable field photos: flush any queued maintenance/housekeeping photo
// uploads when connectivity returns (and once on load, in case we start online
// with a backlog). Best-effort — never blocks the app. Inspections has its own
// frame queue flushed from its capture screen.
import { flushUploads } from './lib/uploadQueue';
window.addEventListener('online', () => { flushUploads().catch(() => {}); });
window.addEventListener('load', () => { flushUploads().catch(() => {}); });
// R38: iOS never refires 'load' on a bfcache restore, and the common failure
// is "connected but dead cellular" where 'online' never fires either — so the
// legacy queue also flushes on pageshow + return-to-visible. (The new photo
// uploader in lib/photoUploader registers its own identical triggers.)
window.addEventListener('pageshow', () => { flushUploads().catch(() => {}); });
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') flushUploads().catch(() => {}); });
